import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getDashboardStats } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { formatRelativeTime } from "../lib/utils";
import {
  Users, TrendingUp, TrendingDown, UserCheck, Clock, ArrowRight,
  FileSpreadsheet, UserPlus, RefreshCw, Target, BarChart3,
  PieChartIcon, Activity, AlertCircle, Bookmark, Zap,
  ArrowUpRight, ArrowDownRight, Minus,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Area, AreaChart,
} from "recharts";

const COLORS = ['#8B5CF6','#3B82F6','#10B981','#F59E0B','#EC4899','#EF4444','#14B8A6','#6366F1'];

// ─── Mini KPI Card ────────────────────────────────────────────────
const KpiCard = ({ title, value, sub, icon: Icon, iconColor, testId, trend, warning }) => {
  const trendIcon = trend > 0 ? <ArrowUpRight className="h-3 w-3 text-green-500" /> :
                    trend < 0 ? <ArrowDownRight className="h-3 w-3 text-red-500" /> :
                    trend === 0 ? <Minus className="h-3 w-3 text-muted-foreground" /> : null;
  const trendColor = trend > 0 ? "text-green-600" : trend < 0 ? "text-red-500" : "text-muted-foreground";
  return (
    <Card className={`hover:-translate-y-1 transition-transform duration-200 ${warning ? "border-orange-400 dark:border-orange-600" : ""}`} data-testid={testId}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <Icon className={`h-5 w-5 ${iconColor}`} />
      </CardHeader>
      <CardContent>
        <div className={`text-3xl font-bold ${warning ? "text-orange-500" : ""}`}>{value}</div>
        <div className="flex items-center gap-1 mt-1">
          {trendIcon && <span className={`text-xs flex items-center gap-0.5 ${trendColor}`}>{trendIcon}{Math.abs(trend)}%</span>}
          {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Funnel bar (status pipeline) ────────────────────────────────
const FunnelBar = ({ name, count, maxCount, color }) => {
  const pct = maxCount > 0 ? Math.round((count / maxCount) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="w-28 text-xs text-right text-muted-foreground truncate shrink-0">{name}</div>
      <div className="flex-1 relative h-7 bg-muted rounded-md overflow-hidden">
        <div
          className="absolute top-0 left-0 h-full rounded-md transition-all duration-700"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
        <span className="absolute inset-0 flex items-center pl-2 text-xs font-medium text-white mix-blend-exclusion">
          {count} leads
        </span>
      </div>
      <div className="w-10 text-xs text-right shrink-0 font-medium">{pct}%</div>
    </div>
  );
};

// ─── Conversion row ───────────────────────────────────────────────
const ConvRow = ({ name, total, converted, rate, color, badge }) => (
  <div className="space-y-1">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 min-w-0">
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
          style={{ backgroundColor: color }}>
          {name.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-medium text-sm truncate">{name}</span>
            {badge && <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-300 shrink-0">{badge}</span>}
          </div>
          <p className="text-xs text-muted-foreground">{converted} conv. / {total}</p>
        </div>
      </div>
      <span className={`ml-2 shrink-0 text-sm font-semibold px-2 py-0.5 rounded-full ${rate >= 20 ? "bg-green-100 text-green-700" : "bg-muted text-muted-foreground"}`}>
        {rate}%
      </span>
    </div>
    <div className="relative h-1.5 bg-muted rounded-full overflow-hidden">
      <div className="absolute top-0 left-0 h-full rounded-full transition-all duration-500"
        style={{ width: `${Math.min(rate, 100)}%`, backgroundColor: badge ? "#f59e0b" : (rate >= 20 ? "#22c55e" : color) }} />
    </div>
  </div>
);

export const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isAdmin, user } = useAuth();
  const navigate = useNavigate();

  const fetchStats = async () => {
    setLoading(true);
    try {
      const r = await getDashboardStats();
      setStats(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  const weekTrend = stats?.leadsLastWeek > 0
    ? Math.round(((stats.leadsThisWeek - stats.leadsLastWeek) / stats.leadsLastWeek) * 100)
    : stats?.leadsThisWeek > 0 ? 100 : null;

  const getActionLabel = (a) => ({
    CREATE_LEAD: "Nouveau lead", UPDATE_LEAD: "Lead mis à jour", DELETE_LEAD: "Lead supprimé",
    CREATE_NOTE: "Note ajoutée", UPDATE_NOTE: "Note modifiée", IMPORT_CSV: "Import CSV",
    BULK_ASSIGN_LEADS: "Attribution en masse", ROUND_ROBIN_ASSIGN: "Distribution auto",
    CREATE_USER: "Utilisateur créé", EXPORT_CSV: "Export CSV",
    LEADFLOW_INBOUND: "Lead reçu via LeadFlow",
  }[a] || a);

  const donutData = stats ? [
    { name: "Leads frais", value: stats.totalLeadsFrais ?? stats.totalLeads, fill: "#3B82F6" },
    { name: "Data", value: stats.totalLeadsData ?? 0, fill: "#F59E0B" },
  ].filter(d => d.value > 0) : [];

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-64" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">

      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Bonjour, {user?.name}</h1>
          <p className="text-muted-foreground mt-1">Tableau de bord — {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchStats}>
            <RefreshCw className="h-4 w-4 mr-2" />Actualiser
          </Button>
          {isAdmin && (
            <Button size="sm" onClick={() => navigate("/import")} data-testid="import-cta">
              <FileSpreadsheet className="h-4 w-4 mr-2" />Importer
            </Button>
          )}
        </div>
      </div>

      {/* ── KPI Grid ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3" data-testid="kpi-grid">
        <KpiCard testId="stat-total-leads" title="Total leads" value={stats?.totalLeads ?? 0}
          sub={isAdmin ? "dans la base" : "attribués à vous"} icon={Users} iconColor="text-primary" />
        <KpiCard testId="stat-this-week" title="Cette semaine" value={stats?.leadsThisWeek ?? 0}
          sub={`sem. préc. : ${stats?.leadsLastWeek ?? 0}`} icon={TrendingUp} iconColor="text-green-500"
          trend={weekTrend} />
        <KpiCard testId="stat-this-month" title="Ce mois" value={stats?.leadsThisMonth ?? 0}
          sub="nouveaux leads" icon={Clock} iconColor="text-blue-500" />

        {isAdmin ? (
          <>
            <KpiCard testId="stat-conversion-frais" title="Transfo frais"
              value={stats?.conversionRateFrais != null ? `${stats.conversionRateFrais}%` : "-"}
              sub={`${stats?.convertedLeadsFrais ?? 0} / ${stats?.totalLeadsFrais ?? 0}`}
              icon={Target} iconColor="text-orange-500" />
            {stats?.totalLeadsData != null && (
              <KpiCard testId="stat-conversion-data" title="Transfo data"
                value={stats?.conversionRateData != null ? `${stats.conversionRateData}%` : "-"}
                sub={`${stats?.convertedLeadsData ?? 0} / ${stats?.totalLeadsData ?? 0}`}
                icon={Target} iconColor="text-amber-500" />
            )}
            <KpiCard testId="stat-unassigned" title="Non assignés"
              value={stats?.unassignedLeads ?? 0}
              sub="leads sans vendeur" icon={AlertCircle} iconColor="text-orange-500"
              warning={(stats?.unassignedLeads ?? 0) > 0} />
          </>
        ) : (
          <>
            <KpiCard testId="stat-conversion-seller" title="Ma conversion"
              value={`${stats?.conversionRate ?? 0}%`}
              sub={`${stats?.convertedLeads ?? 0} convertis`} icon={Target} iconColor="text-green-500" />
          </>
        )}

        <KpiCard testId="stat-marked" title="Leads marqués"
          value={stats?.markedLeads ?? 0} sub="étoilés" icon={Bookmark} iconColor="text-yellow-500" />

        {isAdmin && (
          <KpiCard testId="stat-avg-seller" title="Moy. / vendeur"
            value={stats?.avgLeadsPerSeller ?? 0}
            sub={`${stats?.sellerPerformance?.filter(s => s.isActive).length ?? 0} vendeurs actifs`}
            icon={UserCheck} iconColor="text-primary" />
        )}
      </div>

      {/* ── Évolution + Donut + Pipeline ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Area chart évolution */}
        {stats?.leadsOverTime && stats.leadsOverTime.length > 0 && (
          <Card className="lg:col-span-2" data-testid="chart-leads-over-time">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-primary" />
                <CardTitle className="text-base">Évolution des leads</CardTitle>
              </div>
              <CardDescription>Nouveaux leads par semaine (12 dernières semaines)</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={stats.leadsOverTime}>
                  <defs>
                    <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} formatter={(v) => [v, "Leads"]} />
                  <Area type="monotone" dataKey="count" stroke="#8B5CF6" strokeWidth={2} fillOpacity={1} fill="url(#colorLeads)" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Donut Data vs Frais */}
        {donutData.length > 0 && (
          <Card data-testid="chart-data-frais">
            <CardHeader>
              <div className="flex items-center gap-2">
                <PieChartIcon className="h-5 w-5 text-amber-500" />
                <CardTitle className="text-base">Répartition Data / Frais</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={donutData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                    innerRadius={55} outerRadius={80}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}>
                    {donutData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-4 mt-2">
                {donutData.map(d => (
                  <div key={d.name} className="flex items-center gap-1.5 text-xs">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.fill }} />
                    <span className="text-muted-foreground">{d.name} — <strong>{d.value}</strong></span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── Entonnoir pipeline ── */}
      {stats?.leadsByStatus?.length > 0 && (
        <Card data-testid="funnel-pipeline">
          <CardHeader>
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              <CardTitle className="text-base">Pipeline — Entonnoir de conversion</CardTitle>
            </div>
            <CardDescription>Volume de leads à chaque étape du cycle de vente</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.leadsByStatus.map((s) => (
                <FunnelBar key={s.statusId} name={s.statusName} count={s.count}
                  maxCount={Math.max(...stats.leadsByStatus.map(x => x.count))}
                  color={s.statusColor} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Performance vendeurs ── */}
      {isAdmin && stats?.sellerPerformance && stats.sellerPerformance.length > 0 && (
        <Card data-testid="seller-performance">
          <CardHeader>
            <div className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-primary" />
              <CardTitle className="text-base">Performance commerciaux</CardTitle>
            </div>
            <CardDescription>Leads frais / data et taux de transformation par vendeur</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left py-2 pr-4 font-medium">Commercial</th>
                    <th className="text-center py-2 px-3 font-medium text-blue-600 dark:text-blue-400">Frais</th>
                    <th className="text-center py-2 px-3 font-medium text-blue-600 dark:text-blue-400">% Frais</th>
                    {stats.sellerPerformance.some(s => s.totalLeadsData != null && s.totalLeadsData > 0) && (
                      <>
                        <th className="text-center py-2 px-3 font-medium text-amber-600 dark:text-amber-400">Data</th>
                        <th className="text-center py-2 px-3 font-medium text-amber-600 dark:text-amber-400">% Data</th>
                      </>
                    )}
                    <th className="text-center py-2 pl-3 font-medium">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.sellerPerformance.map((s, i) => (
                    <tr key={s.sellerId} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                            style={{ backgroundColor: COLORS[i % COLORS.length] }}>
                            {s.sellerName.charAt(0).toUpperCase()}
                          </div>
                          <span className="font-medium truncate max-w-[130px]">{s.sellerName}</span>
                          {!s.isActive && <Badge variant="secondary" className="text-xs py-0">inactif</Badge>}
                        </div>
                      </td>
                      <td className="text-center py-3 px-3">
                        <div className="text-blue-700 dark:text-blue-300 font-medium">{s.totalLeadsFrais ?? s.totalLeads}</div>
                        <div className="text-xs text-muted-foreground">{s.convertedLeadsFrais ?? s.convertedLeads} conv.</div>
                      </td>
                      <td className="text-center py-3 px-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${(s.conversionRateFrais ?? s.conversionRate) >= 20 ? "bg-green-100 text-green-700" : "bg-muted text-muted-foreground"}`}>
                          {s.conversionRateFrais ?? s.conversionRate}%
                        </span>
                      </td>
                      {stats.sellerPerformance.some(sp => sp.totalLeadsData != null && sp.totalLeadsData > 0) && (
                        <>
                          <td className="text-center py-3 px-3">
                            <div className="text-amber-700 dark:text-amber-300 font-medium">{s.totalLeadsData ?? 0}</div>
                            <div className="text-xs text-muted-foreground">{s.convertedLeadsData ?? 0} conv.</div>
                          </td>
                          <td className="text-center py-3 px-3">
                            {(s.totalLeadsData ?? 0) > 0 ? (
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${(s.conversionRateData ?? 0) >= 20 ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                                {s.conversionRateData ?? 0}%
                              </span>
                            ) : <span className="text-xs text-muted-foreground">—</span>}
                          </td>
                        </>
                      )}
                      <td className="text-center py-3 pl-3 font-semibold">{s.totalLeads}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Distribution vendeurs (donut) + leads par campagne ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {isAdmin && stats?.leadsBySeller?.length > 0 && (
          <Card data-testid="chart-leads-by-seller">
            <CardHeader>
              <div className="flex items-center gap-2">
                <PieChartIcon className="h-5 w-5 text-primary" />
                <CardTitle className="text-base">Distribution par vendeur</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={stats.leadsBySeller} dataKey="count" nameKey="sellerName"
                    cx="50%" cy="50%" outerRadius={80}
                    label={({ sellerName, percent }) => `${sellerName} ${(percent * 100).toFixed(0)}%`}>
                    {stats.leadsBySeller.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {isAdmin && stats?.leadsByCampaign?.length > 0 && (
          <Card data-testid="chart-leads-by-campaign">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-indigo-500" />
                <CardTitle className="text-base">Leads par campagne</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={stats.leadsByCampaign} layout="vertical" margin={{ left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="campaignName" type="category" tick={{ fontSize: 11 }} width={110} />
                  <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} />
                  <Bar dataKey="count" name="Leads" radius={[0, 4, 4, 0]}>
                    {stats.leadsByCampaign.map((e, i) => <Cell key={i} fill={e.campaignColor || COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── Conversion Source + Campagne ── */}
      {isAdmin && (stats?.conversionBySource?.length > 0 || stats?.conversionByCampaign?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {stats?.conversionBySource?.length > 0 && (
            <Card data-testid="conversion-by-source">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-green-500" />
                  <CardTitle className="text-base">Conversion par source</CardTitle>
                </div>
                <CardDescription>Taux de transformation par origine des leads</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stats.conversionBySource.map((s, i) => (
                    <ConvRow key={s.sourceId} name={s.sourceName} total={s.totalLeads}
                      converted={s.convertedLeads} rate={s.conversionRate}
                      color={COLORS[i % COLORS.length]} />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {stats?.conversionByCampaign?.length > 0 && (
            <Card data-testid="conversion-by-campaign">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-blue-500" />
                  <CardTitle className="text-base">Conversion par campagne</CardTitle>
                </div>
                <CardDescription>Leads frais (bleu) et data (orange) distingués</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stats.conversionByCampaign.map((c, i) => (
                    <ConvRow key={c.campaignId} name={c.campaignName} total={c.totalLeads}
                      converted={c.convertedLeads} rate={c.conversionRate}
                      color={c.campaignColor || COLORS[i % COLORS.length]}
                      badge={c.isData ? "Data" : null} />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ── Activité récente ── */}
      <Card data-testid="recent-activity">
        <CardHeader>
          <CardTitle className="text-base">Activité récente</CardTitle>
          <CardDescription>10 dernières actions sur la plateforme</CardDescription>
        </CardHeader>
        <CardContent>
          {stats?.recentActivity?.length > 0 ? (
            <div className="space-y-3">
              {stats.recentActivity.map((a, i) => (
                <div key={a.id || i} className="flex items-center gap-3 py-1.5 border-b border-border last:border-0">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Activity className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{getActionLabel(a.actionType)}</p>
                    <p className="text-xs text-muted-foreground">
                      {a.performedByName || "Système"} · {formatRelativeTime(a.createdAt)}
                    </p>
                  </div>
                  {a.metadata?.leadName && (
                    <Badge variant="outline" className="hidden sm:inline-flex text-xs">{a.metadata.leadName}</Badge>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">Aucune activité récente</div>
          )}
        </CardContent>
      </Card>

      {/* ── Actions rapides (vendeur) ── */}
      {!isAdmin && (
        <Card data-testid="quick-actions">
          <CardHeader><CardTitle className="text-base">Actions rapides</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {[
              { label: "Voir mes leads", icon: Users, path: "/leads" },
              { label: "Ajouter un lead", icon: UserPlus, path: "/leads?new=true" },
              { label: "Pipeline kanban", icon: TrendingUp, path: "/kanban" },
            ].map(({ label, icon: Icon, path }) => (
              <Button key={path} variant="outline" className="w-full justify-between" onClick={() => navigate(path)}>
                <span className="flex items-center gap-2"><Icon className="h-4 w-4" />{label}</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
